import React, { useState } from "react";
import { ShieldCheck, FileSearch, Loader2 } from "lucide-react";

export const DIMS = [["coverage", "Coverage"], ["restrictions", "Restrictions"], ["waiting", "Waiting periods"], ["claims", "Claims & service"], ["affordability", "Affordability"], ["suitability", "Suitability"]];
const CHIP = {
  covered: "bg-[#E8F3EE] text-[#0B4D3A]", not_covered: "bg-[#F6E9E6] text-[#8A3B2E]", not_mentioned: "bg-stone-100 text-stone-500",
  not_verified: "bg-[#F7F0DA] text-[#7A6219]", not_applicable: "bg-stone-100 text-stone-500",
};
const LABEL = { covered: "Covered", not_covered: "Not covered", not_mentioned: "Not mentioned", not_verified: "Not verified", not_applicable: "Not applicable" };

const NEUTRAL_DIMS = ["restrictions", "waiting"];
/** Restrictions / waiting periods that are "covered" are not benefits — show a neutral "Applies" chip. */
export const StatusChip = ({ status, dimension, testId }) => {
  const neutral = status === "covered" && NEUTRAL_DIMS.includes(dimension);
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${neutral ? "bg-[#F7F0DA] text-[#7A6219]" : CHIP[status] || CHIP.not_mentioned}`} data-testid={testId}>{neutral ? "Applies" : LABEL[status] || status}</span>;
};

const Cell = ({ cell, dimension, onOpen }) => {
  if (!cell) return <td className="px-3 py-2.5 text-stone-400">—</td>;
  const noValue = !cell.value;
  return (
    <td className="px-3 py-2.5 align-top">
      <StatusChip status={cell.status} dimension={dimension} />
      {!noValue && <div className="text-sm text-stone-900 mt-1 leading-snug">{cell.value}</div>}
      {cell.verified === false && cell.status !== "not_mentioned" && <div className="text-[10px] text-[#7A6219] mt-0.5">AI-extracted · not verified</div>}
      {cell.verified === true && <div className="text-[10px] text-[#0B4D3A] mt-0.5 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> verified</div>}
      {(cell.simple || cell.page) && (
        <button type="button" onClick={() => onOpen(cell)} className="block text-[10px] text-stone-500 hover:text-[#0B4D3A] mt-1 underline decoration-dotted underline-offset-2">
          {cell.page ? `p.${cell.page}${cell.clause ? ` · ${cell.clause}` : ""}` : "explain"}
        </button>
      )}
    </td>
  );
};

/** Deterministic six-dimension comparison. `cmp` = /lens2/compare payload (status done). */
export default function SixDimensionTable({ cmp, testId = "six-dim" }) {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [dim, setDim] = useState("");
  const [open, setOpen] = useState(null);
  if (!cmp) return null;
  if (cmp.status === "processing") return <div className="flex items-center gap-2 text-sm text-[#0B4D3A] py-8 justify-center" data-testid={`${testId}-loading`}><Loader2 className="w-4 h-4 animate-spin" /> {cmp.progress || "Reading the documents…"}</div>;
  if (cmp.status === "failed") return <div className="text-sm text-[#8A3B2E] py-4" data-testid={`${testId}-failed`}>Comparison failed{cmp.error ? ` — ${cmp.error}` : ""}. Try again.</div>;
  const cols = cmp.columns || [];
  const rows = (cmp.rows || []).filter((r) => (!onlyDiff || r.meaningful) && (!dim || r.dimension === dim));
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 text-xs font-bold text-stone-700 cursor-pointer rounded-full border border-stone-200 bg-white px-3 py-1.5">
          <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} data-testid={`${testId}-only-diff`} /> Show only meaningful differences
        </label>
        <div className="flex flex-wrap gap-1">
          <button type="button" onClick={() => setDim("")} className={`rounded-full px-3 py-1.5 text-xs font-bold ${!dim ? "bg-[#0B4D3A] text-white" : "bg-white border border-stone-200 text-stone-600"}`} data-testid={`${testId}-dim-all`}>All</button>
          {DIMS.map(([k, l]) => <button key={k} type="button" onClick={() => setDim(k)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${dim === k ? "bg-[#0B4D3A] text-white" : "bg-white border border-stone-200 text-stone-600"}`} data-testid={`${testId}-dim-${k}`}>{l}</button>)}
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-xs min-w-[700px]">
          <thead>
            <tr className="bg-[#FBF8F1]">
              <th className="sticky left-0 bg-[#FBF8F1] text-left px-3 py-3 text-[10px] font-black uppercase tracking-wider text-stone-500 w-44">Provision</th>
              {cols.map((c) => (
                <th key={c.id} className="text-left px-3 py-3 align-top min-w-[200px]" data-testid={`${testId}-col-${c.id}`}>
                  <div className="font-black text-stone-900 leading-tight">{c.label}</div>
                  <div className="text-[9px] uppercase tracking-wider font-bold text-stone-400 mt-0.5">{c.kind === "current" ? "Customer's policy" : c.kind === "crece" ? "Crece-distributed plan" : c.kind === "uploaded" ? "Uploaded policy" : "Market plan"}</div>
                  <div className="text-[10px] text-stone-500 mt-0.5">{c.sheet_only ? "Crece product sheet · not IRDAI-verified" : c.verified ? "Verified provisions" : c.verified === false ? "AI-extracted · not verified" : "From the customer's document"}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DIMS.map(([k, l]) => {
              const drows = rows.filter((r) => r.dimension === k);
              if (!drows.length) return null;
              return (
                <React.Fragment key={k}>
                  <tr className="bg-[#F1EDE2]/70"><td colSpan={cols.length + 1} className="px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-[#0B4D3A]" data-testid={`${testId}-group-${k}`}>{l}</td></tr>
                  {drows.map((r) => (
                    <tr key={r.key} className="border-t border-stone-100" data-testid={`${testId}-row-${r.key}`}>
                      <td className="sticky left-0 bg-white px-3 py-2.5 font-bold text-stone-700 align-top">
                        <span className="inline-flex items-center gap-1.5">{r.meaningful && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" title="Differs in a way that matters" data-testid={`${testId}-diff-${r.key}`} />}{r.label}</span>
                      </td>
                      {cols.map((c) => <Cell key={c.id} cell={(r.cells || {})[c.id]} dimension={r.dimension} onOpen={(cell) => setOpen({ ...cell, label: r.label, col: c.label })} />)}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!rows.length && <tr><td colSpan={cols.length + 1} className="px-3 py-6 text-center text-stone-400">No rows match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-stone-500">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> difference that matters at claim time</span>
        <span>Grey chips = the document does not say / not yet verified — never a zero</span>
        {cmp.seconds != null && <span className="ml-auto">Built in {cmp.seconds}s</span>}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4" onClick={() => setOpen(null)} data-testid={`${testId}-clause-modal`}>
          <div className="bg-[#FBF8F1] rounded-2xl p-6 max-w-lg w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] font-black uppercase tracking-wider text-[#0B4D3A]">{open.col}</div>
            <div className="font-display text-lg font-black mt-1">{open.label}</div>
            <div className="mt-2 text-sm text-stone-900">{open.value || LABEL[open.status]}</div>
            {open.simple && <p className="mt-3 text-sm text-stone-700 leading-relaxed">{open.simple}</p>}
            {open.impact && <p className="mt-2 text-sm text-stone-700 leading-relaxed"><b>Impact:</b> {open.impact}</p>}
            {open.quote && <blockquote className="mt-3 border-l-2 border-[#D4AF37] pl-3 text-xs italic text-stone-600">“{open.quote}”</blockquote>}
            <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-stone-500"><FileSearch className="w-3 h-3" /> {open.page ? `Page ${open.page}` : "Page not cited"}{open.clause ? ` · ${open.clause}` : ""}{open.confidence != null ? ` · confidence ${Math.round(open.confidence * 100)}%` : ""}</div>
          </div>
        </div>
      )}
    </div>
  );
}
